import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Checkbox, FormControlLabel, Grid, IconButton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  FeatureSubscriptionType,
  PortalPermissions,
  useFeatureSubscription_GetFeatureSubscriptionTypeQuery,
  User_CreateUpdateUserInput,
} from 'queries';
import { Eye } from 'ts-react-feather-icons';

import FormPhoneField from 'common/form-phone-field';
import FormTextField from 'common/form-text-field';
import Spinner from 'common/spinner';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';
import useHasPortalPermission from 'utils/useHasPortalPermission';
import useIsPortalOwner from 'utils/useIsPortalOwner';

import CancelSubscriptionModal from './components/cancel-subscription-modal';

const useStyles = makeStyles(() => ({
  formContainer: {
    display: 'flex',
  },
  buttonContainer: {
    float: 'right',
    marginBottom: '10vh',
    width: '45vw',
  },
  divider: {
    backgroundColor: '#F5DE76',
    width: 'auto',
    position: 'relative',
    marginLeft: '20px',
    marginRight: '20px',
  },
  title: {
    color: '#096094',
    marginBottom: '1vh',
  },
  saveButton: {
    backgroundColor: '#F5DE76',
    marginRight: '2.5vw',
    float: 'right',
  },
  cancelButton: {
    color: '#ffffff',
    backgroundColor: '#8DA8B2',
    float: 'right',
  },
  checkbox: {
    padding: '2vh 0',
  },
  inputGrid: {
    paddingRight: '20px',
  },
}));

const ProfileForm = () => {
  const classes = useStyles();
  const history = useHistory();
  const { portalId } = useParams<Record<string, string>>();

  const isPortalOwner = useIsPortalOwner();
  const hasPortalPermissionClient = useHasPortalPermission(PortalPermissions.Client);

  const [showPassword, setShowPassword] = useState(false);
  const [showCancelSubscriptionModal, setShowCancelSubscriptionModal] = useState(false);

  const {
    control,
    formState: { errors },
  } = useFormContext<User_CreateUpdateUserInput>();

  const {
    data: featureSubscriptionTypeData,
    loading: featureSubscriptionTypeLoading,
    refetch: refetchFeatureSubscriptionType,
  } = useFeatureSubscription_GetFeatureSubscriptionTypeQuery({
    variables: {
      portalId,
    },
    onError: (e) => {
      showAlert({
        message: 'Error getting feature subscription type: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  if (!featureSubscriptionTypeData && featureSubscriptionTypeLoading) {
    return <Spinner />;
  }

  const onCancelSubscriptionComplete = () => {
    refetchFeatureSubscriptionType();
  };

  const handleOnUpgradeSubscriptionClick = async () => {
    history.push(`/home/${portalId}/subscription-checkout?isUpdating=True`);
  };

  const handleOnBuySubscriptionClick = () => {
    history.push(`/home/${portalId}/subscription-pricing-table`);
  };

  return (
    <>
      <Grid container>
        <Grid spacing={3} xs={12} item md={6} className={classes.inputGrid}>
          <Controller
            name="firstName"
            control={control}
            render={({ field: { ref, ...field } }) => (
              <FormTextField
                {...field}
                inputRef={ref}
                errorMessage={errors.firstName?.message}
                label="First Name"
              />
            )}
          />
        </Grid>
        <Grid spacing={3} xs={12} item md={6}>
          <Controller
            name="lastName"
            control={control}
            render={({ field: { ref, ...field } }) => (
              <FormTextField
                {...field}
                inputRef={ref}
                errorMessage={errors.lastName?.message}
                label="Last Name"
              />
            )}
          />
        </Grid>
        <Grid spacing={3} xs={12} item md={6} className={classes.inputGrid}>
          <Controller
            name="email"
            control={control}
            render={({ field: { ref, ...field } }) => (
              <FormTextField
                {...field}
                inputRef={ref}
                errorMessage={errors.email?.message}
                label="Email"
                disabled={hasPortalPermissionClient}
              />
            )}
          />
        </Grid>
        <Grid spacing={3} xs={12} item md={6}>
          <Controller
            name="phone"
            control={control}
            render={({ field: { name, onBlur, onChange, value } }) => (
              <FormPhoneField
                name={name}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                errorMessage={errors.phone?.message}
                label="Phone Number"
              />
            )}
          />
        </Grid>
        <Grid spacing={3} xs={12} item md={6} className={classes.inputGrid}>
          <Controller
            name="password"
            control={control}
            render={({ field: { ref, ...field } }) => (
              <FormTextField
                {...field}
                inputRef={ref}
                type={showPassword ? 'text' : 'password'}
                errorMessage={errors.password?.message}
                label="New Password"
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ padding: '8px' }}
                      size="large"
                    >
                      <Eye />
                    </IconButton>
                  ),
                }}
              />
            )}
          />
        </Grid>
        <Grid spacing={3} xs={12} item md={6}>
          <Controller
            name="confirmPassword"
            control={control}
            render={({ field: { ref, ...field } }) => (
              <FormTextField
                {...field}
                inputRef={ref}
                type="password"
                errorMessage={errors.confirmPassword?.message}
                label="Confirm New Password"
              />
            )}
          />
        </Grid>
        <Grid spacing={3} xs={12} item md={12}>
          <Controller
            name="enableStripePayments"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                className={classes.checkbox}
                control={<Checkbox color="primary" checked={field.value || false} />}
                name="enableStripePayments"
                label="Enable Stripe payments"
                // @ts-expect-error - Property 'checked' does not exist on type 'EventTarget'.
                onChange={(val) => field.onChange(val.target.checked)}
              />
            )}
          />
        </Grid>
        {isPortalOwner && featureSubscriptionTypeData!.data !== FeatureSubscriptionType.None && (
          <Grid spacing={2} container>
            <Grid item>
              <Button
                component="span"
                color="primary"
                variant="contained"
                size="small"
                onClick={handleOnUpgradeSubscriptionClick}
              >
                Upgrade Subscription
              </Button>
            </Grid>
          </Grid>
        )}
        {isPortalOwner && featureSubscriptionTypeData!.data === FeatureSubscriptionType.None && (
          <Grid spacing={3} xs={12} md={12} item>
            <Button
              component="span"
              color="primary"
              variant="contained"
              size="small"
              onClick={handleOnBuySubscriptionClick}
            >
              Buy a Subscription
            </Button>
          </Grid>
        )}
      </Grid>
      <CancelSubscriptionModal
        setShowModal={setShowCancelSubscriptionModal}
        showModal={showCancelSubscriptionModal}
        onCancelSubscriptionComplete={onCancelSubscriptionComplete}
        onExitClick={() => setShowCancelSubscriptionModal(false)}
      />
    </>
  );
};

export default ProfileForm;
